import type { MapDocument } from "../shared/types";
import type { VenueMapPinGroup } from "./VenueMapPins";
import type { VenueMapRouteData } from "./VenueMapRoute";
import type { MapFocusTarget } from "./venueMapFocus";
import { venueMapPinAnchor } from "./venueMapPinAnchor";
import {
  mapStandHeight,
  projectMapDepth,
  projectMapPoint,
  type VenueMapCamera,
} from "./venueMapProjection";

type FramingPoint = {
  x: number;
  y: number;
  depth: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
};

/** Fit one visible wayfinding leg, including raised endpoint roofs and pins. */
export function fitVenueMapRoute(
  document: MapDocument,
  route: VenueMapRouteData,
  groups: readonly VenueMapPinGroup[],
  baseline: VenueMapCamera,
  width: number,
  height: number,
  bottomInset = 0,
): (MapFocusTarget & { zoom: number; framingScale: number }) | null {
  if (
    width <= 0 ||
    height <= 0 ||
    baseline.imageWidth <= 0 ||
    !Number.isFinite(width + height + baseline.imageWidth + bottomInset) ||
    route.fromStopId === route.toStopId ||
    route.points.length < 2 ||
    route.points.some(
      (point) =>
        !Number.isFinite(point.x + point.y) ||
        point.x < 0 ||
        point.x > 1 ||
        point.y < 0 ||
        point.y > 1,
    ) ||
    !route.points.some(
      (point) =>
        Math.hypot(point.x - route.points[0].x, point.y - route.points[0].y) >
        1e-9,
    )
  )
    return null;

  const { geometry } = document;
  const endpoints = [route.fromStopId, route.toStopId].map((id) => ({
    location: document.locations.find((location) => location.id === id),
    stand: geometry.stands.find((stand) => stand.id === id),
    group: groups.find((group) => group.id === id),
  }));
  if (
    endpoints.some(
      ({ location, stand }) => !location || location.hidden || stand?.hidden,
    )
  )
    return null;

  const camera: VenueMapCamera = {
    ...baseline,
    stageWidth: 0,
    stageHeight: 0,
    offsetX: 0,
    offsetY: 0,
    panX: 0,
    panY: 0,
  };
  const margin = Math.min(18, width * 0.05, height * 0.05);
  const topInset = Math.min(56, height * 0.18);
  const bottom = Math.max(0, Math.min(height * 0.6, bottomInset));
  const project = (point: {
    x: number;
    y: number;
    z?: number;
  }): FramingPoint => {
    const projected = projectMapPoint(camera, point);
    return {
      x: projected.x / baseline.imageWidth,
      y: projected.y / baseline.imageWidth,
      depth:
        camera.perspective || camera.pitch !== 0
          ? projectMapDepth(camera, point)
          : 1,
      left: margin,
      right: margin,
      top: margin + topInset,
      bottom: margin + bottom,
    };
  };
  const geometryPoints = route.points.map((point) =>
    project({ ...point, z: mapStandHeight("exhibitor") / 2 }),
  );
  const pinPoints: FramingPoint[] = [];
  for (const { location, stand, group } of endpoints) {
    const elevation = stand ? mapStandHeight(stand) : 0;
    if (stand) {
      for (const [x, y] of stand.polygons.flat()) {
        for (const z of [0, elevation]) {
          geometryPoints.push(
            project({ x: x / geometry.width, y: y / geometry.height, z }),
          );
        }
      }
    }
    const anchor = project({
      ...venueMapPinAnchor(location!, geometry),
      z: elevation,
    });
    geometryPoints.push(anchor);
    const count = Math.max(1, group?.stops.length || 0);
    const columns = Math.min(3, count);
    const rows = Math.ceil(count / 3);
    const pinWidth = columns * 44 + (columns - 1) * 3 + (count > 1 ? 6 : 0);
    const pinHeight = rows * 44 + (rows - 1) * 3 + (count > 1 ? 14 : 8);
    pinPoints.push({
      ...anchor,
      left: margin + pinWidth / 2,
      right: margin + pinWidth / 2,
      top: margin + topInset + pinHeight,
    });
  }

  const intervals = (imageWidth: number, points: readonly FramingPoint[]) => {
    let minX = -Infinity;
    let maxX = Infinity;
    let minY = -Infinity;
    let maxY = Infinity;
    for (const point of points) {
      minX = Math.max(
        minX,
        (-width / 2 + point.left - point.x * imageWidth) / point.depth,
      );
      maxX = Math.min(
        maxX,
        (width / 2 - point.right - point.x * imageWidth) / point.depth,
      );
      minY = Math.max(
        minY,
        (-height / 2 + point.top - point.y * imageWidth) / point.depth,
      );
      maxY = Math.min(
        maxY,
        (height / 2 - point.bottom - point.y * imageWidth) / point.depth,
      );
    }
    return { minX, maxX, minY, maxY, fits: minX <= maxX && minY <= maxY };
  };
  let points = [...geometryPoints, ...pinPoints];
  // An upright group larger than the viewport cannot fit at any map scale.
  // Still frame the complete leg instead of leaving the camera unchanged.
  if (!intervals(0, points).fits) points = geometryPoints;
  let lower = 0;
  let upper = baseline.imageWidth * 4;
  for (let attempt = 0; attempt < 40; attempt++) {
    const middle = (lower + upper) / 2;
    if (intervals(middle, points).fits) lower = middle;
    else upper = middle;
  }
  const framing = intervals(lower, points);
  const factor = lower / baseline.imageWidth;
  return {
    anchor: { x: 0.5, y: 0.5, z: 0 },
    at: {
      x: width / 2 + (framing.minX + framing.maxX) / 2,
      y: height / 2 + (framing.minY + framing.maxY) / 2,
    },
    zoom: Math.max(1, Math.min(4, factor)),
    framingScale: Math.min(1, factor),
  };
}
